import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiCopy, FiAlertTriangle } from 'react-icons/fi';
import instanciaAxios, { esErrorPermiso } from '../../miscelaneo/instanciaAxios';
import { useContextoSesion } from '../../miscelaneo/ContextoSesion';
import FuncionesGenerales from '../util/FuncionesGenerales';
import { calcularFechaMinimaEmisionISO } from '../../miscelaneo/facturacionHelpers';
import PlantillaModal from '../util/PlantillaModal';
import SpinnerCargando from '../util/SpinnerCargando';

// Código interno de "Factura de Consumo" (E32), usado como comprobante por
// defecto cuando la factura original era demo y el usuario decide generar
// la copia con NCF (no hay un tipo de comprobante original que heredar).
const TIPO_COMPROBANTE_CONSUMO_DEFECTO = '2';

function crearClaveIdempotenciaFiscal(prefijo = 'factura-dup') {
  return `${prefijo}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function ModalDuplicarFactura({ abierto, onCerrar, idFactura, onDuplicada }) {
  const { usuario } = useContextoSesion();
  const { obtenerFechaActual } = FuncionesGenerales(usuario);

  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [factura, setFactura] = useState(null);
  const [fechaEmision, setFechaEmision] = useState('');
  const [tipoDestino, setTipoDestino] = useState('mismo'); // 'mismo' | 'demo' | 'fiscal'

  useEffect(() => {
    if (!abierto || !idFactura) {
      setFactura(null);
      setTipoDestino('mismo');
      return;
    }

    const cargar = async () => {
      setCargando(true);
      try {
        const resp = await instanciaAxios.get(`/factura/${idFactura}`);
        setFactura(resp.data);
        setFechaEmision(obtenerFechaActual());
        setTipoDestino('mismo');
      } catch (error) {
        if (esErrorPermiso(error)) return;
        console.error('Error al cargar factura para duplicar:', error);
        toast.error('Error al cargar la factura original');
        onCerrar();
      } finally {
        setCargando(false);
      }
    };

    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, idFactura]);

  if (!abierto) return null;

  const esOriginalFiscal = !!factura?.ncf;
  // 'mismo' respeta el tipo original (demo sigue demo, fiscal sigue fiscal).
  const seraFiscal = tipoDestino === 'mismo' ? esOriginalFiscal : tipoDestino === 'fiscal';
  const cambiaDeTipo = (tipoDestino === 'fiscal' && !esOriginalFiscal) || (tipoDestino === 'demo' && esOriginalFiscal);

  const handleConfirmar = async () => {
    if (!factura || guardando) return;

    if (!fechaEmision) {
      toast.error('Debe indicar la fecha de emisión');
      return;
    }
    if (fechaEmision > obtenerFechaActual()) {
      toast.error('La fecha de emisión no puede ser futura');
      return;
    }
    if (fechaEmision < calcularFechaMinimaEmisionISO()) {
      toast.error('La fecha de emisión no puede ser anterior a 1.5 meses atrás');
      return;
    }

    setGuardando(true);
    try {
      const detallesFactura = (factura.detalles_factura || []).map((det) => ({
        id_producto: det.id_producto,
        descripcion_producto: det.descripcion,
        cantidad: parseFloat(det.cantidad),
        precio: parseFloat(det.precio),
        descuento: parseFloat(det.descuento) || 0,
        indicador_facturacion: det.indicador_facturacion,
        itbis_tasa: parseFloat(det.itbis_tasa),
        indicador_bien_servicio: det.id_tipo_producto || 1
      }));

      const tipoComprobante = seraFiscal
        ? (factura.id_tipo_comprobante_fiscal
          ? String(factura.id_tipo_comprobante_fiscal)
          : TIPO_COMPROBANTE_CONSUMO_DEFECTO)
        : null;

      const claveIdempotencia = crearClaveIdempotenciaFiscal('factura-dup');

      const payload = {
        id_entidad_cliente: factura.id_entidad_cliente,
        id_entidad_usuario: usuario.id_entidad,
        id_sucursal: factura.id_sucursal,
        id_tipo_fiscal: 1,
        tipo_comprobante: tipoComprobante,
        dgii_id_tipo_ingreso: factura.dgii_id_tipo_ingreso || '01',
        dgii_id_tipo_pago: factura.dgii_id_tipo_pago || '1',
        dgii_id_forma_pago: factura.dgii_id_forma_pago || '01',
        monto: parseFloat(factura.monto),
        descuento_total: parseFloat(factura.descuento_total) || 0,
        impuesto_total: parseFloat(factura.impuesto_total),
        monto_total: parseFloat(factura.monto_total),
        fecha_vencimiento: fechaEmision,
        fecha_emision: fechaEmision,
        comentario: factura.comentario,
        detallesFactura,
        clave_idempotencia: claveIdempotencia,
        incluirPagos: false
      };

      const respuesta = await instanciaAxios.post('/factura/', payload, {
        headers: { 'x-idempotency-key': claveIdempotencia }
      });

      if (respuesta.status === 201 && respuesta.data?.data) {
        const datos = respuesta.data.data;

        if (respuesta.data.pendienteEnvio === true) {
          if (datos.dgii?.es_error_certificado) {
            toast.error(
              `⚠️ CERTIFICADO DIGITAL RECHAZADO POR DGII\n\nLa factura duplicada (NCF: ${datos.ncf}) se creó, pero DGII no la pudo recibir: ${datos.dgii?.mensaje || 'certificado vencido o revocado'}.\n\nActualice el certificado y reenvíe desde Pendientes DGII.`,
              { autoClose: false, closeOnClick: false }
            );
          } else {
            toast.warning(
              `Factura duplicada con NCF: ${datos.ncf}. Envío a DGII pendiente${datos.dgii?.mensaje ? `: ${datos.dgii.mensaje}` : ''}.`,
              { autoClose: 12000 }
            );
          }
        } else if (datos.ncf) {
          toast.success(`Factura duplicada exitosamente con NCF: ${datos.ncf}`);
        } else {
          toast.success('Factura duplicada exitosamente (sin comprobante fiscal)');
        }

        onDuplicada?.(datos.id_factura);
        onCerrar();
      }
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al duplicar factura:', error);

      if (error.response?.status === 409) {
        const codigo = error.response?.data?.codigo;
        if (codigo === 'SIN_SECUENCIA_E' || codigo === 'SIN_SECUENCIA_B') {
          toast.error(
            '⚠️ No hay secuencias NCF disponibles para este tipo de comprobante. Configure una nueva secuencia en Configuración > Secuencias NCF, o duplique como factura demo.',
            { autoClose: 8000 }
          );
          return;
        }
      }

      toast.error(error.response?.data?.mensaje || 'Error al duplicar la factura');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <PlantillaModal mostrar={abierto} onClose={onCerrar} titulo="Duplicar Factura">
      {cargando || !factura ? (
        <div className="py-8"><SpinnerCargando /></div>
      ) : (
        <div className="space-y-5 min-w-[420px]">
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm">
            <p><span className="font-medium">Factura original:</span> #{factura.id_factura} {factura.ncf ? `(NCF: ${factura.ncf})` : '(sin comprobante fiscal)'}</p>
            <p><span className="font-medium">Cliente:</span> {factura.cliente_razon_social || factura.nombre_cliente}</p>
            <p><span className="font-medium">Monto total:</span> RD$ {parseFloat(factura.monto_total || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Fecha de emisión de la copia
            </label>
            <input
              type="date"
              value={fechaEmision}
              min={calcularFechaMinimaEmisionISO()}
              max={obtenerFechaActual()}
              onChange={(e) => setFechaEmision(e.target.value)}
              className="w-full px-3 py-2 border border-gray-400 rounded-xl focus:ring-2 focus:ring-color-primario focus:border-color-primario"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Tipo de comprobante para la copia
            </label>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="tipoDestino"
                  checked={tipoDestino === 'mismo'}
                  onChange={() => setTipoDestino('mismo')}
                />
                Igual que la original ({esOriginalFiscal ? 'con NCF' : 'demo, sin NCF'})
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="tipoDestino"
                  checked={tipoDestino === 'demo'}
                  onChange={() => setTipoDestino('demo')}
                />
                Demo (sin comprobante fiscal)
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="tipoDestino"
                  checked={tipoDestino === 'fiscal'}
                  onChange={() => setTipoDestino('fiscal')}
                />
                Fiscal (genera NCF y envía a DGII)
              </label>
            </div>
          </div>

          <div className={`p-3 rounded-xl flex gap-2 border ${
            cambiaDeTipo ? 'bg-amber-50 border-amber-300' : 'bg-blue-50 border-blue-200'
          }`}>
            <FiAlertTriangle className={`flex-shrink-0 mt-0.5 ${cambiaDeTipo ? 'text-amber-600' : 'text-blue-600'}`} />
            <p className={`text-sm ${cambiaDeTipo ? 'text-amber-800' : 'text-blue-800'}`}>
              {seraFiscal ? (
                cambiaDeTipo ? (
                  <>
                    La original era <strong>demo</strong>. Esta copia se generará{' '}
                    {factura.id_tipo_comprobante_fiscal
                      ? 'con comprobante fiscal'
                      : <>como <strong>Factura de Consumo (E32)</strong> por defecto</>}
                    , se le asignará un NCF nuevo y se enviará a la DGII. Verifique que el cliente
                    tenga los datos fiscales (RNC/cédula) correctos antes de continuar.
                  </>
                ) : (
                  <>
                    Esta copia se generará <strong>con comprobante fiscal</strong> (igual que la
                    original): se le asignará un <strong>NCF nuevo</strong> y se enviará a la DGII.
                  </>
                )
              ) : (
                cambiaDeTipo ? (
                  <>
                    La original tiene <strong>comprobante fiscal</strong>. Esta copia se creará
                    como <strong>demo (sin NCF)</strong> y no se reportará a la DGII.
                  </>
                ) : (
                  <>
                    Esta copia se creará <strong>como demo (sin NCF)</strong> (igual que la
                    original) y no se reportará a la DGII.
                  </>
                )
              )}
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t">
            <button
              onClick={onCerrar}
              disabled={guardando}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmar}
              disabled={guardando}
              className="px-4 py-2 bg-color-primario text-white rounded-xl hover:bg-opacity-90 transition-colors inline-flex items-center gap-2 disabled:opacity-60"
            >
              <FiCopy size={16} />
              {guardando ? 'Duplicando...' : 'Duplicar factura'}
            </button>
          </div>
        </div>
      )}
    </PlantillaModal>
  );
}
